import { onMounted, onBeforeUnmount, ref, watch, type Ref } from 'vue';

type Controls = {
	isPlaying: Ref<boolean>;
	play: () => void;
	pause: () => void;
};
type Options = {
	pauseThreshold?: number;
	resumeThreshold?: number;
	usePageVisibility?: boolean;
};

export function useViewportAutoplay(
	targetEl: Ref<HTMLElement | null>,
	{ isPlaying, play, pause }: Controls,
	opts: Options = {}
) {
	const pauseThreshold = opts.pauseThreshold ?? 0.35;
	const resumeThreshold = opts.resumeThreshold ?? 0.45;
	const usePageVisibility = opts.usePageVisibility ?? true;

	const pausedByVisibility = ref(false);
	const lastRatio = ref(0);

	const pauseForVisibility = () => {
		if (isPlaying.value) {
			pause();
			pausedByVisibility.value = true;
		}
	};
	const resumeFromVisibility = () => {
		if (pausedByVisibility.value) {
			play();
			pausedByVisibility.value = false;
		}
	};

	let io: IntersectionObserver | null = null;

	function attach(el: HTMLElement) {
		detach();
		if (!('IntersectionObserver' in window)) return;
		io = new IntersectionObserver(
			(entries) => {
				const e = entries[0];
				if (!e) return;
				lastRatio.value = e.intersectionRatio;
				if (e.isIntersecting && lastRatio.value >= resumeThreshold) {
					resumeFromVisibility();
				} else if (lastRatio.value <= pauseThreshold) {
					pauseForVisibility();
				}
			},
			{ threshold: [0, pauseThreshold, resumeThreshold, 1] }
		);
		io.observe(el);
	}

	function detach() {
		io?.disconnect();
		io = null;
	}

	onMounted(() => {
		const stopWatch = watch(targetEl, (el) => (el ? attach(el) : detach()), {
			immediate: true,
		});

		let onVis: (() => void) | null = null;
		if (usePageVisibility) {
			onVis = () => {
				if (document.visibilityState === 'hidden') {
					pauseForVisibility();
				} else if (
					pausedByVisibility.value &&
					lastRatio.value >= resumeThreshold
				) {
					resumeFromVisibility();
				}
			};
			document.addEventListener('visibilitychange', onVis, { passive: true });
		}

		onBeforeUnmount(() => {
			stopWatch();
			detach();
			if (onVis) document.removeEventListener('visibilitychange', onVis);
		});
	});

	return { pausedByVisibility, lastRatio };
}
